import { useState } from "react";

const useLeaseFormState = () => {
  // Basic Information State
  const [basicInfo, setBasicInfo] = useState({
    unitNumber: "",
    unitName: "",
    unitType: "",
    landlordId: "",
    tenant: "",
    status: "Active",
    isNewTenant: false,
    selectedCurrency: null,
    availableCurrencies: []
  });

  // Lease Terms State
  const [leaseTerms, setLeaseTerms] = useState({
    startDate: "",
    endDate: "",
    leaseDuration: "",
    autoRenewal: false,
    leaseTemplate: "",
    escalations: []
  });

  // Financial Terms State
  const [financialTerms, setFinancialTerms] = useState({
    monthlyRent: "",
    paymentDueDate: "",
    paymentMethods: [{ type: "", details: {}, isPrimary: true }],
    securityDeposit: "",
    balanceBroughtForward: "0",
    requireLandlordApproval: false,
    selectedPenaltyId: null,
    selectedReminderId: null,
    depositMonths: 1,
    manualDepositEntry: false,
    taxEnabled: false,
    digitaxEnabled: false,
    // Property manager revenue eligibility for this unit (overrides the
    // facility defaults in Revenue Settings when rates are provided)
    rentCommissionEligible: true,
    rentCommissionType: "percentage",
    rentCommissionValue: "",
    leaseFeeEligible: true,
    leaseFeeType: "percentage",
    leaseFeeValue: ""
  });

  // Rent Escalations State
  const [escalations] = useState([]);

  // Billing Cycle State
  const [billingCycle, setBillingCycle] = useState({
    billingFrequency: "Monthly",
    nextInvoiceDate: "",
    autoSend: false
  });

  // Bank Details State (kept for backward compatibility)
  const [bankDetails] = useState([]);

  // GL Accounts State
  const [glAccounts, setGlAccounts] = useState({
    invoice: {
      debit: null,
      credit: null
    },
    payment: {
      debit: null,
      credit: null
    }
  });

  // Update functions for each section
  const updateBasicInfo = (updates) => {
    setBasicInfo(prev => ({ ...prev, ...updates }));
  };

  const updateLeaseTerms = (updates) => {
    setLeaseTerms(prev => ({ ...prev, ...updates }));
  };

  const updateFinancialTerms = (updates) => {
    setFinancialTerms(prev => ({ ...prev, ...updates }));
  };

  const updateBillingCycle = (updates) => {
    setBillingCycle(prev => ({ ...prev, ...updates }));
  };

  const updateGLAccounts = (updates) => {
    setGlAccounts(prev => ({ ...prev, ...updates }));
  };

  // Validation functions for each section
  const validateBasicInfo = () => {
    const errors = [];

    if (!basicInfo.unitNumber) errors.push("Unit is required");
    if (!basicInfo.tenant) errors.push("Tenant information is required - please select a unit with an assigned tenant");
    if (!basicInfo.landlordId) errors.push("Landlord information is required - please select a unit with an assigned landlord");

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const validateLeaseTerms = () => {
    const errors = [];

    if (!leaseTerms.startDate) errors.push("Start date is required");
    if (!leaseTerms.endDate) errors.push("End date is required");
    if (!leaseTerms.leaseDuration || parseInt(leaseTerms.leaseDuration) <= 0) {
      errors.push("Valid lease duration is required");
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const validateFinancialTerms = () => {
    const errors = [];

    if (!basicInfo.selectedCurrency) errors.push("Currency is required");
    if (!financialTerms.monthlyRent || parseFloat(financialTerms.monthlyRent) <= 0) {
      errors.push("Valid monthly rent is required");
    }
    if (!financialTerms.paymentDueDate || parseInt(financialTerms.paymentDueDate) < 1 || parseInt(financialTerms.paymentDueDate) > 31) {
      errors.push("Valid payment due date (1-31) is required");
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const validatePaymentMethods = () => {
    const errors = [];

    if (financialTerms.paymentMethods.length === 0) {
      errors.push("At least one payment method is required");
    }

    const validPaymentMethod = financialTerms.paymentMethods.every(method => {
      if (!method.type) {
        errors.push("Payment method type is required");
        return false;
      }

      if (method.type === "Bank Transfer") {
        if (!method.bankDetailsId) {
          errors.push("Please select a bank account for bank transfer payment method");
          return false;
        }
      }

      if (method.type === "Mobile Money") {
        if (!method.details?.businessNumber) {
          errors.push("Business number is required for mobile money");
          return false;
        }
      }

      if (method.type === "Cheque") {
        if (!method.details?.bankToDraft) {
          errors.push("Bank to draft cheque is required");
          return false;
        }
      }
      return true;
    });

    return {
      isValid: errors.length === 0 && validPaymentMethod,
      errors
    };
  };

  const validateBillingCycle = () => {
    const errors = [];

    if (!billingCycle.billingFrequency) {
      errors.push("Billing frequency is required");
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  return {
    // Main form data object expected by components
    formData: {
      basicInfo,
      leaseTerms,
      financialTerms,
      billingCycle,
      glAccounts,
      escalations: leaseTerms.escalations
    },

    // Update functions grouped
    updateFormData: {
      updateBasicInfo,
      updateLeaseTerms,
      updateFinancialTerms,
      updateBillingCycle,
      updateGLAccounts
    },

    // Validation functions grouped
    validation: {
      validateBasicInfo,
      validateLeaseTerms,
      validateFinancialTerms,
      validatePaymentMethods,
      validateBillingCycle,
      validateAll: () => {
        const basicValidation = validateBasicInfo();
        const leaseValidation = validateLeaseTerms();
        const financialValidation = validateFinancialTerms();
        const paymentValidation = validatePaymentMethods();
        const billingValidation = validateBillingCycle();

        const allErrors = [
          ...basicValidation.errors,
          ...leaseValidation.errors,
          ...financialValidation.errors,
          ...paymentValidation.errors,
          ...billingValidation.errors
        ];

        return {
          isValid: allErrors.length === 0,
          errors: allErrors
        };
      }
    },

    // Individual state for special cases
    escalations,
    bankDetails
  };
};

export default useLeaseFormState;